import { DateService } from '@core/services/date.service';
import { MaxBookingTimeStatus } from '@features/booking/calender/model/maxBookingTimeStatus';
import { addCalendarDays, toCalendarDay } from '@shared/models/dateTypes';

import { TimeslotCheckAvailabilityResponse } from '../../models/timeslot-check-availability-reponse';

/**
 * The message for a maximum booking time the server will refuse — otherwise null.
 *
 * The availability check *reports* the quota, it does not enforce it: an administrator may book past
 * it, and for them the quota is no error to show. Only the quota that actually blocks gets a
 * message — and it belongs in the basket next to the calendar, where the times can be corrected,
 * rather than as a refusal further into the checkout flow.
 */
export function blockingMaxBookingTimeMessage(
  res: TimeslotCheckAvailabilityResponse | null,
): string | null {
  const status = res?.maxBookingTime;

  if (!status?.exceeded || status.canOverride) {
    return null;
  }

  return status.message ?? 'Maksimumtiden for prisperioden er brugt op.';
}

/** The basket's own minutes against one quota in one window. */
interface QuotaTotal {
  status: MaxBookingTimeStatus;
  requestedMinutes: number;
  lines: number;
}

/**
 * The basket's total against the maximum booking time: each quota on its own, with the basket's own
 * lines added up.
 *
 * The availability check knows only one period at a time, and the basket's lines have not been
 * created yet — so two hours that are each under the quota can together be over it. The server only
 * discovers that when the periods are saved, and by then it is too late: the user is already moving
 * on through the flow. So they are added up here, while the times are still on screen.
 *
 * Lines are grouped per quota and window (`scheduleId` + `windowStart`), because those are the ones
 * sharing the number — also when they sit in different resources under the same price period. The
 * key is the price period's id and not its name: two independent price periods can share a name,
 * and two separate budgets would then be added together and block a booking that is within both.
 */
export function blockingBasketMaxBookingTime(
  res: TimeslotCheckAvailabilityResponse[],
  dateService: DateService,
): string | null {
  const totals = new Map<string, QuotaTotal>();

  for (const item of res ?? []) {
    const status = item?.maxBookingTime;

    // No quota on the resource, or one the current user may book past: nothing to block.
    if (!status || status.canOverride) {
      continue;
    }

    const key = quotaKey(status);
    const total = totals.get(key);

    if (total) {
      total.requestedMinutes += status.requestedMinutes;
      total.lines += 1;
    } else {
      totals.set(key, { status, requestedMinutes: status.requestedMinutes, lines: 1 });
    }
  }

  for (const { status, requestedMinutes, lines } of totals.values()) {
    // `usedMinutes` is the same for every line in the group: same quota, same window, same owner.
    const total = status.usedMinutes + requestedMinutes;

    if (total <= status.maxMinutes) {
      continue;
    }

    // When a single time blows the quota on its own, the server has already written the sentence —
    // it is the source of the wording. Only the sum of several lines, which the server cannot see,
    // is written here.
    if (lines === 1 && status.exceeded && status.message) {
      return status.message;
    }

    return quotaExceededMessage(status, total, dateService);
  }

  return null;
}

/**
 * The same sentence the API sends for one period, now for the basket's total. The wording is agreed
 * with Ballerup and has to be the same wherever it is composed — see `BookingPeriodService` and
 * `BookingLimits` in the API.
 */
function quotaExceededMessage(
  status: MaxBookingTimeStatus,
  totalMinutes: number,
  dateService: DateService,
): string {
  return (
    `Du har samlet brugt ${formatQuotaMinutes(totalMinutes)} af de maks. ` +
    `${formatQuotaMinutes(status.maxMinutes)}, der kan bookes pr. ${status.period} ` +
    `${formatQuotaWindow(status, dateService)}. ` +
    `Du har tidligere brugt ${formatQuotaMinutes(status.usedMinutes)}.`
  );
}

/** The quota and window lines are added up in: one maximum booking time over one period. */
export const quotaKey = (status: MaxBookingTimeStatus) => `${status.scheduleId}|${status.windowStart}`;

/** Minutes as a quota is spoken of — mirrors `BookingLimits.FormatMinutes` in the API. */
export function formatQuotaMinutes(minutes: number): string {
  const whole = Math.round(minutes);
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;

  if (hours === 0) {
    return `${rest} min`;
  }

  if (rest !== 0) {
    return `${hours} t ${rest} min`;
  }

  return hours === 1 ? '1 time' : `${hours} timer`;
}

/**
 * The window the quota resets in — mirrors `FormatWindow` in the API: a day is named by its date, a
 * week and a month by their outer days (the end date is the day after the period, so one day is
 * subtracted).
 */
function formatQuotaWindow(status: MaxBookingTimeStatus, dateService: DateService): string {
  // Calendar-day arithmetic throughout: no instant is built, so there is no frame for the browser
  // to supply and nothing that has to cancel out. This used to parse with `new Date()` and read
  // the result back with local getters — correct, because both halves were browser-local, but
  // correct by cancellation rather than by construction, and silently wrong the day the API
  // attached an offset to either value.
  const start = toCalendarDay(status.windowStart);

  // `period` is the API's own label for the span — see `BookingLimits.PeriodLabel`.
  if (status.period === 'døgn') {
    return `den ${dateService.formatCalendarDay(start)}`;
  }

  // The API's window end is the day *after* the period, so the last day inside it is one back.
  const end = addCalendarDays(toCalendarDay(status.windowEnd), -1);

  return `(${dateService.formatCalendarDay(start)} – ${dateService.formatCalendarDay(end)})`;
}
